import React from 'react';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import AdCard from '../components/Ads/AdCard';
import InfiniteScrollSection from '../components/InfiniteScrollSection';
import {
  GET_FEATURED_ADS,
  GET_TRENDING_ADS,
} from '../graphql/queries';
import {
  SparklesIcon,
  ChevronRightIcon,
  FireIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { Row, Col } from 'react-bootstrap';

const Home: React.FC = () => {
  const { data: featuredData, loading: featuredLoading } = useQuery(GET_FEATURED_ADS, {
    variables: { limit: 10 },
  });
  const { data: trendingData, loading: trendingLoading } = useQuery(GET_TRENDING_ADS, {
    variables: { limit: 10 },
  });

  const featuredAds = featuredData?.featuredAds || [];
  const trendingAds = trendingData?.trendingAds || [];

  const categories = [
    { name: 'Mobile', icon: '📱', slug: 'mobile-phones', color: '#ffecd8' },
    { name: 'Cars', icon: '🚗', slug: 'cars', color: '#ffe8e8' },
    { name: 'Bikes', icon: '🏍️', slug: 'motorcycles', color: '#e8f4ff' },
    { name: 'Property', icon: '🏠', slug: 'property-sale', color: '#e8ffe8' },
    { name: 'Laptops', icon: '💻', slug: 'laptops-computers', color: '#f0e8ff' },
    { name: 'Furniture', icon: '🛋️', slug: 'furniture', color: '#fff8e8' },
    { name: 'Fashion', icon: '👕', slug: 'mens-fashion', color: '#ffe8f4' },
    { name: 'More', icon: '✨', slug: 'categories', color: '#e8fff8' },
  ];

  const MobileSection: React.FC<{
    title: string;
    ads: any[];
    loading: boolean;
    viewAllLink: string;
  }> = ({ title, ads, loading, viewAllLink }) => {
    if (loading) {
      return (
        <section className="mb-6">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle"
                style={{ width: 6, height: 24, background: 'var(--pw-primary)' }}
              />
              <h2 className="fs-4 fw-bold mb-0" style={{ color: 'var(--pw-text)' }}>
                {title}
              </h2>
            </div>
          </div>
          <div
            className="d-flex gap-3 overflow-auto pb-3"
            style={{ marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16 }}
          >
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0" style={{ width: 170 }}>
                <div className="skeleton mb-3" style={{ aspectRatio: '1/1', borderRadius: 16 }} />
                <div className="skeleton mb-2" style={{ height: 18, width: '80%', borderRadius: 8 }} />
                <div className="skeleton" style={{ height: 14, width: '50%', borderRadius: 6 }} />
              </div>
            ))}
          </div>
        </section>
      );
    }

    if (ads.length === 0) return null;

    return (
      <section className="mb-6">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle"
              style={{ width: 6, height: 24, background: 'var(--pw-primary)' }}
            />
            <h2 className="fs-4 fw-bold mb-0" style={{ color: 'var(--pw-text)' }}>
              {title}
            </h2>
          </div>
          <Link
            to={viewAllLink}
            className="d-flex align-items-center fw-semibold text-decoration-none"
            style={{ fontSize: 14, color: 'var(--pw-primary)' }}
          >
            See all <ChevronRightIcon style={{ width: 18, height: 18, marginLeft: 4 }} />
          </Link>
        </div>
        <div
          className="d-flex gap-3 overflow-auto pb-3"
          style={{ marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16 }}
        >
          {ads.map((ad) => (
            <motion.div
              key={ad.id}
              className="flex-shrink-0"
              style={{ width: 180 }}
              whileHover={{ scale: 1.03, y: -4 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <AdCard ad={ad} compact />
            </motion.div>
          ))}
        </div>
      </section>
    );
  };

  const DesktopGridSection: React.FC<{
    title: string;
    ads: any[];
    viewAllLink: string;
    icon?: React.ReactNode;
    iconBg?: string;
  }> = ({ title, ads, viewAllLink, icon, iconBg }) => {
    if (ads.length === 0) return null;

    return (
      <section className="py-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            {icon && (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: iconBg || 'var(--pw-surface-tint)',
                  color: 'var(--pw-primary)',
                }}
              >
                {icon}
              </div>
            )}
            <h2 className="fs-3 fw-bold mb-0" style={{ color: 'var(--pw-text)' }}>
              {title}
            </h2>
          </div>
          <Link
            to={viewAllLink}
            className="d-flex align-items-center fw-semibold text-decoration-none"
            style={{ fontSize: 15, color: 'var(--pw-primary)' }}
          >
            View all <ChevronRightIcon style={{ width: 18, height: 18, marginLeft: 4 }} />
          </Link>
        </div>
        <Row className="g-3 g-md-4">
          {ads.map((ad: any) => (
            <Col key={ad.id} xs={6} md={4} lg={4} xl={3}>
              <AdCard ad={ad} />
            </Col>
          ))}
        </Row>
      </section>
    );
  };

  return (
    <Layout>
      <div className="pw-page">
        {/* Desktop Hero Section */}
        <div className="d-none d-md-block mb-4">
          <section
            className="position-relative overflow-hidden"
            style={{
              minHeight: 420,
              borderRadius: 32,
              boxShadow: '0 24px 64px -16px rgba(232, 93, 76, 0.22)',
              background: 'linear-gradient(135deg, #ff9a7a 0%, var(--pw-primary) 45%, #ffbf4d 100%)',
              backgroundSize: '200% 200%',
            }}
          >
            {/* Animated Background Blobs */}
            <div
              className="position-absolute"
              style={{
                top: '10%',
                right: '8%',
                width: 380,
                height: 380,
                background: 'rgba(255,255,255,0.12)',
                borderRadius: '50%',
                filter: 'blur(90px)',
                animation: 'float 8s ease-in-out infinite',
              }}
            />
            <div
              className="position-absolute"
              style={{
                bottom: '10%',
                right: '22%',
                width: 280,
                height: 280,
                background: 'rgba(255,191,77,0.25)',
                borderRadius: '50%',
                filter: 'blur(70px)',
                animation: 'float 10s ease-in-out infinite 2s',
              }}
            />
            <div
              className="position-absolute"
              style={{
                top: '40%',
                right: '35%',
                width: 180,
                height: 180,
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                filter: 'blur(50px)',
                animation: 'float 6s ease-in-out infinite 1s',
              }}
            />

            {/* Hero Content */}
            <div className="position-relative h-100 d-flex align-items-center px-5">
              <div className="text-white position-relative hero-content" style={{ zIndex: 1 }}>
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <div
                    className="d-inline-flex align-items-center gap-2 mb-4 px-4 py-2"
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <SparklesIcon style={{ width: 16, height: 16 }} />
                    <span>NEPAL&apos;S #1 MARKETPLACE</span>
                  </div>
                </motion.div>

                <motion.h1
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="fw-bold mb-4"
                  style={{ fontSize: 52, letterSpacing: '-0.03em', lineHeight: 1.08 }}
                >
                  Buy & Sell
                  <br />
                  Anything in Nepal
                </motion.h1>

                <motion.p
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="mb-5"
                  style={{ fontSize: 18, opacity: 0.95, maxWidth: 460, lineHeight: 1.7 }}
                >
                  Find the best deals near you. From mobile phones to cars, properties to furniture — connect with thousands of buyers and sellers across Nepal.
                </motion.p>

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="d-flex align-items-center gap-4 mb-5"
                >
                  <Link
                    to="/post-ad"
                    className="d-inline-flex align-items-center gap-2 fw-bold text-decoration-none"
                    style={{
                      padding: '16px 32px',
                      borderRadius: 16,
                      background: 'var(--pw-accent)',
                      color: '#5c3d0d',
                      fontSize: 15,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                    }}
                  >
                    Start Selling
                    <ArrowRightIcon style={{ width: 18, height: 18 }} />
                  </Link>
                  <Link
                    to="/search"
                    className="d-inline-flex align-items-center gap-2 fw-semibold text-decoration-none"
                    style={{
                      padding: '16px 32px',
                      borderRadius: 16,
                      background: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      fontSize: 15,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.22)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                    }}
                  >
                    Browse Ads
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>
        </div>

        {/* Mobile Categories */}
        <div className="d-md-none mb-6">
            <div className="d-flex align-items-center gap-2 mb-4">
              <div
                className="rounded-circle"
                style={{ width: 6, height: 24, background: 'var(--pw-primary)' }}
              />
              <h2 className="fs-4 fw-bold mb-0" style={{ color: 'var(--pw-text)' }}>
                Browse by Category
              </h2>
            </div>
            <Row className="g-3">
              {categories.map((cat) => (
                <Col key={cat.slug} xs={3}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="d-flex flex-column align-items-center p-3 text-decoration-none"
                    style={{
                      height: 100,
                      minWidth: 80,
                      background: 'var(--pw-surface)',
                      border: '1px solid var(--pw-border)',
                      borderRadius: 0,
                      boxShadow: '0 4px 14px rgba(116, 67, 35, 0.04)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 8px 22px rgba(116, 67, 35, 0.08)';
                      e.currentTarget.style.borderColor = '#f2d4c0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(116, 67, 35, 0.04)';
                      e.currentTarget.style.borderColor = 'var(--pw-border)';
                    }}
                  >
                    <div
                      className="mb-2 d-flex align-items-center justify-content-center"
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        background: cat.color,
                      }}
                    >
                      <span style={{ fontSize: 24 }}>{cat.icon}</span>
                    </div>
                    <span
                      className="fw-semibold text-center"
                      style={{ fontSize: 11, lineHeight: 1.2, color: 'var(--pw-text-soft)' }}
                    >
                      {cat.name}
                    </span>
                  </Link>
                </Col>
              ))}
            </Row>
          </div>

          {/* Desktop Sections - Layout handles sidebar */}
          <div className="d-none d-md-block">
            <DesktopGridSection
              title="Featured Ads"
              ads={featuredAds}
              viewAllLink="/search?featured=true"
              icon={<FireIcon style={{ width: 24, height: 24 }} />}
              iconBg="rgba(255, 191, 77, 0.15)"
            />
            <DesktopGridSection
              title="Trending Now"
              ads={trendingAds}
              viewAllLink="/search?trending=true"
              icon={<SparklesIcon style={{ width: 24, height: 24 }} />}
              iconBg="rgba(232, 93, 76, 0.1)"
            />
            <InfiniteScrollSection title="Fresh Recommendations" limit={10} />

          </div>

          {/* Mobile Sections */}
          <div className="d-md-none">
            <MobileSection title="Featured Ads" ads={featuredAds} loading={featuredLoading} viewAllLink="/search?featured=true" />
            <MobileSection title="Trending Now" ads={trendingAds} loading={trendingLoading} viewAllLink="/search?trending=true" />
          </div>

        </div>
      </Layout>
  );
};

export default Home;
